import React, { useCallback, useEffect, useState } from "react";
import { DataGridPro, GridColDef } from "@mui/x-data-grid-pro";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  IconButton,
  Tooltip,
  Typography,
  Grid,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import { CustomSwitch } from "../../../../components/Form/CustomSwitch";
import GenericModal from "../../../../components/Modal/GenericModal";
import {
  deleteCollection,
  getCollections,
  moveCollection,
  updateCollection,
} from "../../../../service/BuyerConfiguration/CollectionManagement/collectionService";
import { notifyError, notifySuccess } from "../../../../components/ToastContainer/Toast/Toast";
import AddEditCollection from "../Fragments/AddEditCollection";

const CollectionManagementGrid: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [rows, setRows] = useState<any[]>([]);
  const [openCollectionModal, setOpenCollectionModal] = useState(false);
  const [collectionModalMode, setCollectionModalMode] = useState<"add" | "edit">("add");
  const [selectedCollectionId, setSelectedCollectionId] = useState<number | null>(null);

  const fetchCollections = async () => {
    try {
      const response: any = await getCollections({ page: 1, limit: 100 });
      const payload = response?.data ?? response;
      setRows(Array.isArray(payload?.data) ? payload.data : []);
    } catch (error) {
      notifyError(error?.response?.data?.message || "Failed to fetch collections");
    }
  };

  useEffect(() => {
    fetchCollections();
  }, []);

  const handleRowOrderChange = useCallback(
    async (params: any) => {
      try {
        await moveCollection(params.row.id, params.targetIndex + 1);
        notifySuccess("Collection sequence updated");
        fetchCollections();
      } catch (error: any) {
        notifyError(error?.response?.data?.message || "Failed to reorder collection");
      }
    },
    [],
  );

  const columns: GridColDef[] = [
    { field: "id", headerName: "ID", flex: 0.5, minWidth: 70 },
    { field: "sequence", headerName: "Sequence", flex: 0.7, minWidth: 90 },
    { field: "title", headerName: "Title", flex: 1.2, minWidth: 150 },
    { field: "type", headerName: "Type", flex: 0.7, minWidth: 90 },
    { field: "page", headerName: "Page", flex: 0.7, minWidth: 90 },
    {
      field: "status",
      headerName: "Status",
      flex: 0.9,
      minWidth: 110,
      renderCell: (params) => (
        <CustomSwitch
          checked={Boolean(params.row.status)}
          onChange={async (checked) => {
            try {
              await updateCollection(params.row.id, { status: checked });
              notifySuccess("Status updated");
              fetchCollections();
            } catch (error: any) {
              notifyError(error?.response?.data?.message || "Failed to update status");
            }
          }}
        />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1.2,
      minWidth: 170,
      sortable: false,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            gap: 0.5,
            alignItems: "center",
            height: "100%",
            py: 0.25,
          }}
        >
          <Tooltip title="Manage Entries">
            <IconButton
              color="primary"
              size="small"
              onClick={() => {
                navigate(`/collection-management/${params.row.id}/entries`);
              }}
            >
              <PlaylistAddCheckIcon sx={{ fontSize: "1rem" }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton
              color="primary"
              size="small"
              onClick={() => {
                setCollectionModalMode("edit");
                setSelectedCollectionId(params.row.id);
                setOpenCollectionModal(true);
              }}
            >
              <EditIcon sx={{ fontSize: "1rem" }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              color="error"
              size="small"
              onClick={async () => {
                try {
                  await deleteCollection(params.row.id);
                  notifySuccess("Collection deleted");
                  fetchCollections();
                } catch (error: any) {
                  notifyError(error?.response?.data?.message || "Failed to delete collection");
                }
              }}
            >
              <DeleteIcon sx={{ fontSize: "1rem" }} />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          mb: 2,
          pt: { xs: 0.5, sm: 1 },
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          rowGap: 1.5,
        }}
      >
        <Typography component="h2" variant="h6">
          Collection Management List
        </Typography>
        <Button
          variant="contained"
          sx={{
            width: { xs: "100%", sm: "auto" },
            alignSelf: { xs: "stretch", sm: "center" },
            minHeight: 40,
          }}
          onClick={() => {
            setCollectionModalMode("add");
            setSelectedCollectionId(null);
            setOpenCollectionModal(true);
          }}
        >
          Add Collection
        </Button>
      </Box>

      <Grid
        container
        columns={12}
        sx={{ mb: (theme) => theme.spacing(2), width: "100%" }}
      >
        <Box sx={{ width: "100%", overflowX: "auto" }}>
          <Box sx={{ minWidth: isMobile ? 900 : 1000 }}>
            <DataGridPro
              disableColumnMenu
              autoHeight
              rows={rows}
              columns={columns}
              getRowId={(row) => row.id}
              rowReordering
              onRowOrderChange={handleRowOrderChange}
              sx={{
                "& .MuiDataGrid-cell": {
                  display: "flex",
                  alignItems: "center",
                },
                "& .MuiDataGrid-rowReorderCell": { cursor: "grab" },
                "& .MuiDataGrid-dragIcon": { display: "none !important" },
              }}
            />
          </Box>
        </Box>
      </Grid>

      <GenericModal
        open={openCollectionModal}
        onClose={() => setOpenCollectionModal(false)}
        title={collectionModalMode === "add" ? "Add Collection" : "Edit Collection"}
      >
        <AddEditCollection
          mode={collectionModalMode}
          collectionId={selectedCollectionId}
          onClose={() => setOpenCollectionModal(false)}
          onSaved={fetchCollections}
        />
      </GenericModal>
    </Box>
  );
};

export default CollectionManagementGrid;
